import { Link, useLocation } from 'react-router-dom';
import { Home, Timer, CheckSquare, FolderKanban, Menu, Plus } from 'lucide-react';
import NavItem from '../ui/NavItem';
import useTimerStore from '../../stores/useTimerStore';
import useTaskStore from '../../stores/useTaskStore';
import useUserStore from '../../stores/useUserStore';
import { FOCUS_PATHS } from '../Focus/focusModes';
import './MobileBottomNav.css';

/**
 * MobileBottomNav — Figma "Nav / Bottom Bar · Floating".
 *
 * An inset pill bar (--nav-mobile-bar-inset from all three screen edges,
 * --nav-mobile-bar-radius corners) floating over a bottom fade scrim, with the
 * Quick-Capture FAB as a MEMBER of the bar rather than a separate floating
 * button. Items are the `stacked` layout of the NavItem primitive: 76 tall,
 * 24px icon above a caption label, selected on --nav-mobile-active-radius.
 *
 * Geometry nests exactly: bar radius 28 − bar padding 8 = item radius 20.
 *
 * TABS are split around the FAB — the first `FAB_INDEX` tabs sit to its left,
 * the rest to its right. Module-gated tabs are filtered out first, so the split
 * survives Projects being disabled.
 */

const TABS = [
    { path: '/', label: 'Home', icon: Home, exact: true },
    { path: '/focus', label: 'Focus', icon: Timer },
    { path: '/tasks', label: 'Tasks', icon: CheckSquare },
    { path: '/projects', label: 'Projects', icon: FolderKanban, module: 'projects' },
    { id: 'more', label: 'More', icon: Menu },
];

/** How many (post-filter) tabs sit to the left of the centre FAB. */
const FAB_INDEX = 2;

/** Count badges cap out rather than stretching the 16px pill. */
const BADGE_MAX = 99;

function isTabActive(tab, pathname) {
    if (tab.exact) return pathname === tab.path;
    // Focus owns every mode route — Freestyle, Circuit and Rituals — plus the
    // legacy /flows alias, which redirects to /circuits.
    if (tab.path === '/focus') {
        return FOCUS_PATHS.includes(pathname) || pathname === '/flows';
    }
    return pathname === tab.path || pathname.startsWith(tab.path + '/');
}

export default function MobileBottomNav({ onMorePress, onQuickCapture, hidden }) {
    const location = useLocation();
    const isModuleEnabled = useUserStore(s => s.isModuleEnabled);
    const isTimerRunning = useTimerStore(s => s.isRunning);
    const tasks = useTaskStore(s => s.tasks);
    const pendingCount = tasks.filter(t => t.status !== 'done').length;

    const visibleTabs = TABS.filter(tab => !tab.module || isModuleEnabled(tab.module));

    // QuickCapture listens for this on window; any component may open it.
    const openQuickCapture = onQuickCapture
        || (() => window.dispatchEvent(new CustomEvent('quick-capture-open')));

    // `mobile-bottom-nav__tab[--active]` carry no styling — they are kept as
    // stable hooks for tests and page-level overrides. All appearance comes
    // from NavItem's own classes.
    const renderTab = (tab) => {
        const shared = {
            layout: 'stacked',
            label: tab.label,
            icon: tab.icon,
        };

        if (tab.id === 'more') {
            return (
                <NavItem
                    key="more"
                    {...shared}
                    className="mobile-bottom-nav__tab"
                    onClick={onMorePress}
                    aria-label="More navigation"
                    aria-haspopup="dialog"
                />
            );
        }

        const selected = isTabActive(tab, location.pathname);

        // Focus carries a presence dot while a session runs; Tasks carries the
        // pending count. Neither is a failure, so neither is red (AGENTS.md).
        let badge = null;
        let ariaLabel;
        if (tab.path === '/focus' && isTimerRunning) {
            badge = 'dot';
            ariaLabel = 'Focus, session running';
        } else if (tab.path === '/tasks' && pendingCount > 0) {
            badge = pendingCount > BADGE_MAX ? `${BADGE_MAX}+` : pendingCount;
            ariaLabel = `Tasks, ${pendingCount} open`;
        }

        // `Link`, not `NavLink`, on purpose. NavLink recomputes both `active`
        // and `aria-current` from its own path match and overwrites whatever it
        // is handed — which silently dropped aria-current on the Focus tab
        // while the user sat on /circuits or /flows, the exact routes Focus
        // claims. isTabActive() is the single source of truth here, so the tab
        // uses a plain Link and lets NavItem own selection.
        return (
            <NavItem
                key={tab.path}
                {...shared}
                as={Link}
                to={tab.path}
                selected={selected}
                badge={badge}
                aria-label={ariaLabel}
                className={`mobile-bottom-nav__tab${selected ? ' mobile-bottom-nav__tab--active' : ''}`}
            />
        );
    };

    // `inert`, not just the CSS. --hidden only slides the bar off and fades it
    // out; `pointer-events: none` stops the mouse but does NOT take a node out
    // of the tab order or out of the accessibility tree, so during a fullscreen
    // focus session the five tabs and the FAB stayed keyboard-reachable and
    // screen-reader-visible while completely invisible. `inert` removes the
    // whole subtree from both at once, and dropping the attribute puts it back
    // — no restore handler to keep in sync.
    //
    // React 19 (19.2.4 here) treats `inert` as a real boolean attribute, so
    // `true` emits `inert=""` and `undefined` omits it. `hidden || undefined`
    // rather than a bare `hidden` because React 18 stringified booleans and
    // `inert="false"` is still inert — this way the disabled case is always the
    // absent attribute, whichever runtime renders it.
    return (
        <nav
            className={`mobile-bottom-nav${hidden ? ' mobile-bottom-nav--hidden' : ''}`}
            role="navigation"
            aria-label="Mobile navigation"
            inert={hidden || undefined}
        >
            <div className="mobile-bottom-nav__scrim" aria-hidden="true" />
            <div className="mobile-bottom-nav__bar">
                {visibleTabs.slice(0, FAB_INDEX).map(renderTab)}
                <button
                    type="button"
                    className="mobile-bottom-nav__fab"
                    onClick={openQuickCapture}
                    aria-label="Quick capture"
                >
                    <Plus className="mobile-bottom-nav__fab-icon" aria-hidden="true" focusable="false" />
                </button>
                {visibleTabs.slice(FAB_INDEX).map(renderTab)}
            </div>
        </nav>
    );
}
